import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';

interface ListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  viewCount: number;
  createdAt: number;
}
interface Detail {
  id: string;
  slug: string;
  title: string;
  content: string;
  excerpt: string | null;
  viewCount: number;
  publishedAt: number;
}

/** 平台级 / 英雄纪念堂公共文章栏目（人看 + 爬虫都走 /articles） */
export default function SeoArticles() {
  const { slug } = useParams();
  return slug ? <ArticleDetail slug={slug} /> : <ArticleList />;
}

function ArticleList() {
  const [items, setItems] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ items: ListItem[] }>('/articles?scope=platform').then((res) => {
      if (res.ok && res.data) setItems(res.data.items);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-ink-50/40 text-ink-900">
      <div className="mx-auto max-w-3xl px-5 py-10">
        <header className="mb-8 border-b border-ink-200 pb-5">
          <h1 className="text-2xl font-medium">追思文章</h1>
          <p className="mt-1 text-sm text-ink-500">线上墓迪 · 缅怀与纪念</p>
          <Link
            to="/"
            className="mt-2 inline-block text-xs text-candle-600 hover:underline"
          >
            ← 返回首页
          </Link>
        </header>

        {loading ? (
          <p className="text-sm text-ink-500">加载中…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-ink-500">暂无文章。</p>
        ) : (
          <ul className="space-y-3">
            {items.map((r) => (
              <li
                key={r.id}
                className="rounded-2xl border border-ink-200 bg-white p-5"
              >
                <Link
                  to={`/articles/${r.slug}`}
                  className="font-serifcn text-lg font-medium text-ink-900 hover:text-candle-600"
                >
                  {r.title}
                </Link>
                <p className="mt-1 text-xs text-ink-500">
                  {new Date(r.createdAt * 1000).toLocaleDateString('zh-CN')} · 阅读 {r.viewCount}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink-500">{r.excerpt}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ArticleDetail({ slug }: { slug: string }) {
  const [a, setA] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ article: Detail }>(`/articles/slug/${slug}`).then((res) => {
      if (res.ok && res.data) setA(res.data.article);
      setLoading(false);
    });
  }, [slug]);

  return (
    <div className="min-h-screen bg-ink-50/40 text-ink-900">
      <div className="mx-auto max-w-3xl px-5 py-10">
        <Link
          to="/articles"
          className="text-xs text-candle-600 hover:underline"
        >
          ← 文章列表
        </Link>
        {loading ? (
          <p className="mt-6 text-sm text-ink-500">加载中…</p>
        ) : !a ? (
          <p className="mt-6 text-sm text-ink-500">文章不存在。</p>
        ) : (
          <>
            <h1 className="mt-4 font-serifcn text-3xl font-medium leading-snug">{a.title}</h1>
            <p className="mt-2 text-xs text-ink-500">
              {new Date(a.publishedAt * 1000).toLocaleDateString('zh-CN')} · 阅读 {a.viewCount}
            </p>
            <article className="mt-6 whitespace-pre-wrap text-base leading-loose text-ink-700">
              {a.content}
            </article>
            <div className="mt-10 border-t border-ink-200 pt-5">
              <Link to="/" className="text-sm text-candle-600 hover:underline">
                线上墓迪
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
